import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Play, Check, Clock, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { WalkthroughPlayer } from './walkthrough/WalkthroughPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WalkthroughVideos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState(null);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/walkthrough-videos`, { headers: getHeaders(), cache: 'no-store' })
      .then(r => r.json()).then(d => setVideos(d.videos || [])).catch(() => {}).finally(() => setLoading(false));
  }, [getHeaders]);

  const handleProgress = useCallback((videoId, sectionIndex, completed) => {
    fetch(`${BACKEND_URL}/api/walkthrough-videos/${videoId}/progress`, {
      method: 'POST', headers: getHeaders(), body: JSON.stringify({ section_index: sectionIndex, completed })
    }).catch(() => {});
    if (completed) setVideos(prev => prev.map(v => v.video_id === videoId ? { ...v, watched: true } : v));
  }, [getHeaders]);

  const handleShare = useCallback(async (videoId, videoTitle, e) => {
    e.stopPropagation();
    const url = `${window.location.origin}/watch/${videoId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Link copied — anyone can watch "${videoTitle}"`);
    } catch (_) {
      toast.error('Could not copy — please select and copy the URL manually');
    }
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center py-12 text-slate-400"><div className="w-5 h-5 border-2 border-slate-300 border-t-transparent rounded-full animate-spin mr-2" />Loading videos...</div>;
  }

  return (
    <div className="space-y-6" data-testid="walkthrough-videos">
      <div>
        <h3 className="text-xl font-bold text-slate-900">MyInvestFolio Infrastructure Walkthrough Videos</h3>
        <p className="text-sm text-slate-500 mt-1">Short guided walkthroughs covering the MyInvestFolio concept, features, and how to get started.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {videos.map(video => (
          <Card key={video.video_id} className={`cursor-pointer border transition-all hover:shadow-md hover:-translate-y-0.5 ${activeVideo?.video_id === video.video_id ? 'ring-2' : ''}`}
            style={activeVideo?.video_id === video.video_id ? { borderColor: video.color, '--tw-ring-color': video.color } : {}}
            onClick={() => setActiveVideo(activeVideo?.video_id === video.video_id ? null : video)} data-testid={`video-card-${video.video_id}`}>
            <CardContent className="p-4">
              <div className="rounded-lg h-28 flex items-center justify-center mb-3 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${video.color}15, ${video.color}30)` }}>
                <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-xs" style={{ background: `${video.color}25`, border: `2px solid ${video.color}50` }}>
                  <Play className="w-5 h-5 ml-0.5" style={{ color: video.color }} />
                </div>
                {video.watched && <div className="absolute top-2 right-2"><Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><Check className="w-3 h-3 mr-0.5" /> Watched</Badge></div>}
                <button
                  onClick={(e) => handleShare(video.video_id, video.title, e)}
                  className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/85 hover:bg-white text-slate-700 text-[10px] font-medium shadow-xs border border-slate-200"
                  title="Copy a public link — anyone can watch"
                  data-testid={`share-video-${video.video_id}`}
                >
                  <Share2 className="w-3 h-3" />
                  Share
                </button>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-sm text-slate-800">{video.title}</h4>
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {video.duration_label}</span>
                </div>
                <p className="text-xs text-slate-500 line-clamp-2">{video.subtitle}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      {activeVideo && (
        <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200" data-testid="video-player-container">
          <WalkthroughPlayer video={activeVideo} onClose={() => setActiveVideo(null)} onProgress={handleProgress} />
        </div>
      )}
      {activeVideo && activeVideo.sections && activeVideo.sections.length > 0 && (
        <div className="bg-slate-50 rounded-lg p-4" data-testid="video-sections-list">
          <h4 className="text-sm font-semibold text-slate-700 mb-3">Sections in "{activeVideo.title}"</h4>
          <div className="space-y-2">
            {activeVideo.sections.map((section, i) => (
              <div key={`step-${i}`} className="flex items-start gap-3 text-sm">
                <div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold mt-0.5" style={{ background: `${activeVideo.color}20`, color: activeVideo.color }}>{i + 1}</div>
                <div><span className="font-medium text-slate-700">{section.title}</span><p className="text-xs text-slate-500">{section.description}</p></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
